import { type ReactNode, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { type AddToCartItem, sendAddToCart } from "~/features/cart/cart/api/AddToCart";
import { Card } from "~/features/configurator/Linear/components/Card";
import type { ContractDetailsResponse } from "~/features/contracts/models/models";
import type { AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import { Button, Stack } from "~/common/mui";
import { Link, LinkType } from "~/common/components/Link";
import { isPackageRestrictionEnabled } from "~/features/PackageRestriction/utils/isPackageRestrictionEnabled";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { ImageFormat } from "~/domains/product/types/ProductDataDTO";
import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";
import { fakeDescriptorFromProduct, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import type { PopupData } from "~/features/contracts/components/AddToCartPopup/ContractVersionContents";
import AddContractToCartDialog from "~/features/contracts/components/AddToCartPopup/AddToCartPopup";
import classes from "~/features/contracts/components/ContractDetails/ContractDetailsSummary.module.less";
import { openModal } from "~/common/utils/ModalService";
import CartSuccessModal from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { ContractAddToCartPopupStates } from "~/features/contracts/components/AddToCartPopup/ContractAddToCartPopupStates";
import { Alert } from "~/common/mui/Alert/Alert";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";

interface NewContractDetailsSummaryProps {
  contractDetails: ContractDetailsResponse;
  productAvailability?: AvailabilityInfo;
  className?: string;
}

const transformContractDetailsToATCItem = (
  contractDetails: ContractDetailsResponse,
  popupData: PopupData,
  productAvailability: AvailabilityInfo | undefined,
): AddToCartItem => {
  const { quantity, requestedDeliveryDate } = popupData;

  const price = contractDetails.nettoPrice;
  const packageCode = contractDetails.packCode;
  const packageSize = contractDetails.packSize;

  const packagingInfo =
    productAvailability?.packagingInformation &&
    productAvailability.packagingInformation.filter((item) => item.packCode === packageCode.toString())[0];

  const primaryWarehouseCode = packagingInfo && packagingInfo.primaryWarehouseCode;

  const netPrice = price.value;

  return {
    productCode: contractDetails.product.code,
    packageSize: packageSize,
    packageCode: packageCode.toString(),
    quantity: quantity,
    comment: "",
    netPrice: netPrice,
    priceUnit: 1, // TODO: Change me if contracts provide price unit
    selectedDeliveryDate: requestedDeliveryDate,
    selectedDeliveryDateIsManual: true,
    primaryWarehouseCode: primaryWarehouseCode ?? "",
    contractLine: contractDetails.lineNumber.toString() ?? "",
    contractNumber: contractDetails.schaefflerContractNumber,
    fifteenDigit: false,
    // Contracts are a fixed price. No price reference here
    priceReference: "",
    isPMA: isProductPMA(fakeDescriptorFromProduct(contractDetails.product)),

    restrictedPackage: isPackageRestrictionEnabled(),
    // There is no surcharge in contracts
    surcharge: 0,
    originCountry: "", // TODO: Provide origin country for contracts
    contractOpenQuantity: contractDetails.openQuantity,

    tariffSurchargeApplicable: false,
  };
};

// copied from contract actions, refactored to not use redux
//TODO: investigate how both functions can be brought back together
const contractItemAddToCart = (
  contract: ContractDetailsResponse,
  data: PopupData,
  availability?: AvailabilityInfo,
  onSuccess?: () => void,
  onError?: () => void,
) => {
  const contractItem = transformContractDetailsToATCItem(contract, data, availability);

  if (!contractItem) {
    return undefined;
  }

  AnalyticsMQ.instance.push("addToCart", {
    products: [
      {
        product: contract.product,
        quantity: data.quantity,
        price: {
          ...contract.nettoPrice,
          unit: +contract.priceUnit,
          baseValue: contract.nettoPrice.value,
          surcharge: 0,
        },
        packaging: {
          code: contract.packCode.toString(),
          size: contract.packSize,
        },
      },
    ],
    context: {
      source: "unknown",
    },
  });

  sendAddToCart(contractItem)
    .then(() => {
      onSuccess?.();
    })
    .catch((err) => {
      console.error(err);
      onError?.();
    });
};

const Tile = ({ title, children }: { title: string; children?: ReactNode }) => {
  return (
    <Card className={classes.tile}>
      <Stack gap={2}>
        <div className={classes.tileTitle}>{title}</div>
        <div>{children}</div>
      </Stack>
    </Card>
  );
};

const InfoEntry = ({ label, value }: { label: string; value: ReactNode }) => {
  return (
    <Stack gap={0.5}>
      <div className={classes.infoLabel}>{label}</div>
      <div>{value}</div>
    </Stack>
  );
};

const NetSumTile = ({
  nextDisposition,
  quantity,
  netPrice,
  totalPrice,
  contractDetails,
  predecessor,
  productAvailability,
}: {
  nextDisposition: string;
  quantity: string;
  netPrice: ContractDetailsResponse["nettoPrice"];
  totalPrice: ContractDetailsResponse["totalPrice"];
  contractDetails: ContractDetailsResponse;
  predecessor: { detailsUrl?: string; contractNumber?: string };
  productAvailability?: AvailabilityInfo;
}) => {
  const [contractStatus, setContractStatus] = useState<ContractAddToCartPopupStates>(
    ContractAddToCartPopupStates.LOADED,
  );
  const { val: popup, setTrue: open, setFalse: close } = useBooleanState(false);
  const hasPredecessor = !!predecessor.contractNumber;
  const { t } = useTranslation();

  return (
    <div className={classes.netSumTile}>
      <Stack gap={1} className={classes.netSumInfo}>
        <div className="d-flex flex-justify-between">
          <div>{t("contractOverview.table.disposition")}</div>
          <div>{nextDisposition}</div>
        </div>
        <div className="d-flex flex-justify-between">
          <div>{t("contractDetails.nextDispositionQuantity")}</div>
          <div>{quantity}</div>
        </div>
        <div className="d-flex flex-justify-between">
          <div>{t("common.product.netPricePerPiece:::one")}</div>
          <div>{netPrice.formattedValue}</div>
        </div>
        <div className={classes.netSumTotal}>
          <div className={classes.netSumValue}>
            <p>{t("wishlistPage.total")}</p>
            <p className={classes.infoLabel}>{t("cartPage.taxes")}</p>
          </div>
          <div>{totalPrice.formattedValue}</div>
        </div>
      </Stack>
      <Stack gap={2}>
        <Button
          onClick={open}
          startIcon={<Icon type={IconType.Cart} />}
          disabled={hasPredecessor}
          clta-details-page-element-marker={"add-to-cart"}
          clta-product-name={contractDetails.product.designation}
          clta-product-id={contractDetails.product.sapMaterialNumber}
          clta-total-value={totalPrice.value.toString()}
        >
          {t("recentlyViewProducts.addProducts")}
        </Button>
        {hasPredecessor && (
          <Alert severity="info">
            <p>{t("contractDetails.basedOnPrevious")}</p>
            <Trans i18nKey="contractDetails.pleaseComplete" values={{ contractNumber: predecessor.contractNumber }}>
              Please complete
              <Link className="f-bold" href={predecessor.detailsUrl} variant={LinkType.NAVIGATION}>
                contract {predecessor.contractNumber}
              </Link>
              first.
            </Trans>
          </Alert>
        )}
      </Stack>

      {popup && (
        <AddContractToCartDialog
          contractDetails={contractDetails}
          contractStatus={contractStatus}
          product={contractDetails.product}
          onClose={() => {
            close();
            setContractStatus(ContractAddToCartPopupStates.LOADED);
          }}
          onAdd={(e) => {
            setContractStatus(ContractAddToCartPopupStates.LOADING);
            contractItemAddToCart(
              contractDetails,
              e,
              productAvailability,
              () => {
                close();
                setContractStatus(ContractAddToCartPopupStates.LOADED);
                openModal("add-to-cart-dialog", (close) => (
                  <CartSuccessModal
                    formData={[transformContractDetailsToATCItem(contractDetails, e, productAvailability)]}
                    onClose={close}
                    page="CONTRACT_DETAILS"
                  />
                ));
              },
              () => {
                setContractStatus(ContractAddToCartPopupStates.SEND_FAILURE);
              },
            );
          }}
        />
      )}
    </div>
  );
};

const NewContractDetailsSummary = ({
  contractDetails,
  productAvailability,
  className,
}: NewContractDetailsSummaryProps) => {
  const { t } = useTranslation();
  const nextDisposition =
    contractDetails.nextDisposition && formatDate(contractDetails.nextDisposition, DateFormatType.DATE);
  const contractPeriod =
    contractDetails.contractValidFrom &&
    contractDetails.contractValidTo &&
    `${formatDate(contractDetails.contractValidFrom, DateFormatType.DATE)} - ${formatDate(
      contractDetails.contractValidTo,
      DateFormatType.DATE,
    )}`;
  const datePlaced = contractDetails.datePlaced && formatDate(contractDetails.datePlaced, DateFormatType.DATE);
  const { product, shipToAddress } = contractDetails;
  return (
    <div className={classes.root}>
      <Tile title={t("newContract.contractTile")}>
        <Stack gap={2}>
          <InfoEntry label={t("contractOverview.table.contractNo")} value={contractDetails.customerContractNumber} />
          <InfoEntry
            label={t("contractDetailsPage.summary.schaContractNo")}
            value={contractDetails.schaefflerContractNumber}
          />
          <div className={classes.subGrid}>
            <InfoEntry label={t("contractOverview.table.period")} value={contractPeriod ?? "-"} />
            <InfoEntry label={t("orderDetailsPage.summary.date")} value={datePlaced ?? "-"} />
          </div>
          <InfoEntry
            label={t("contractOverview.table.netPrice")}
            value={<span className="f-bold">{contractDetails.totalPrice.formattedValue}</span>}
          />
          <div className={classes.subGrid}>
            <InfoEntry label={t("contractDetails.totals.totalQty")} value={contractDetails.quantityRequested} />
            <InfoEntry label={t("contractDetails.openQuantity")} value={contractDetails.openQuantity} />
          </div>
        </Stack>
      </Tile>
      <Tile title={t("feedback.option.invoiceProduct")}>
        <Stack gap={2}>
          <Stack gap={2} direction="row">
            <Link href={product.url}>
              <img width={64} src={product.images?.find((image) => image.format === ImageFormat.thumbnail)?.url} />
            </Link>

            <Link variant={LinkType.NAVIGATION} href={product.url} className={classes.alignStart}>
              <span className="f-bold">{product.designation}</span>
            </Link>
          </Stack>
          <InfoEntry
            label={t("addToCart.contract.packaging")}
            value={t("addToCartPopup.packSize", { amount: contractDetails.packSize, code: contractDetails.packCode })}
          />
          <InfoEntry
            label={t("productComparison.download.schaefflerId")}
            value={contractDetails.product.sapMaterialNumber}
          />
          <InfoEntry
            label={t("orderListPage.queryType.customMaterialNumber")}
            value={contractDetails.customerMaterial}
          />
        </Stack>
      </Tile>
      <Tile title={t("newContract.shippingTile")}>
        <Stack gap={2}>
          <InfoEntry
            label={t("checkoutPage.headers.shippingAddress")}
            value={
              <Stack gap={0}>
                <span className="f-bold">{shipToAddress?.companyName}</span>
                <span>{`${shipToAddress?.lines?.map((line) => `${line} `)}`}</span>
                <span>{`${shipToAddress?.postalCode ?? ""} ${shipToAddress?.town ?? ""}`}</span>
                <span>{t(shipToAddress?.country.name)}</span>
              </Stack>
            }
          />
          <InfoEntry label={t("cartPage.shipmentDetails.freightPayment")} value={t("contractDetailsPage.freight")} />
        </Stack>
      </Tile>
      <NetSumTile
        nextDisposition={nextDisposition ?? "-"}
        quantity={contractDetails.nextDispositionQuantity}
        netPrice={contractDetails.nettoPrice}
        totalPrice={contractDetails.totalPrice}
        contractDetails={contractDetails}
        productAvailability={productAvailability}
        predecessor={contractDetails.contractPredecessor}
      />
    </div>
  );
};

export default NewContractDetailsSummary;
